import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUpDown, CheckCircle2, Delete, Gem } from 'lucide-react'
import AnimatedButton from '../../../components/AnimatedButton'

const MIN_BET = 100

const DoubleDesktopBetPanel = ({ isDisabled, isLoading, balance, inventory, onSubmit }) => {
  const { t } = useTranslation()

  const [selectedColor, setSelectedColor] = useState(null)
  const [crystalValue, setCrystalValue] = useState('')
  const [selectedItems, setSelectedItems] = useState([])
  const [sortOrder, setSortOrder] = useState('desc')
  const [errorText, setErrorText] = useState('')

  useEffect(() => {
    const availableItemIds = new Set((inventory || []).map((item) => item.id))
    setSelectedItems((prev) => prev.filter((itemId) => availableItemIds.has(itemId)))
  }, [inventory])

  const parsedCrystal = Number(crystalValue || 0)

  const selectedInventory = useMemo(() => {
    return (inventory || []).filter((item) => selectedItems.includes(item.id))
  }, [inventory, selectedItems])

  const selectedItemsTotal = useMemo(() => {
    return selectedInventory.reduce((sum, item) => sum + Number(item.price ?? item.cost ?? 0), 0)
  }, [selectedInventory])

  const totalBet = parsedCrystal + selectedItemsTotal
  const hasBetInput = parsedCrystal > 0 || selectedItems.length > 0
  const hasInventory = (inventory || []).length > 0
  const missingAmount = Math.max(0, MIN_BET - totalBet)
  const canSubmit = totalBet >= MIN_BET && Boolean(selectedColor) && !isDisabled && !isLoading

  const filteredInventory = useMemo(() => {
    const list = inventory || []

    return [...list].sort((a, b) => {
      const priceA = Number(a.cost || 0)
      const priceB = Number(b.cost || 0)
      return sortOrder === 'asc' ? priceA - priceB : priceB - priceA
    })
  }, [inventory, sortOrder])

  const normalizeCrystal = (value) => {
    if (value === '' || value === null || typeof value === 'undefined') return ''

    const numeric = Number(value)
    const maxBalance = Math.max(0, Number(balance || 0))
    if (!Number.isFinite(numeric)) return ''

    return String(Math.max(0, Math.min(numeric, maxBalance)))
  }

  const toggleItem = (itemId) => {
    setSelectedItems((prev) => (
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    ))
  }

  const handleQuickCrystal = (value) => {
    setCrystalValue(normalizeCrystal(value))
  }

  const clearForm = () => {
    setSelectedItems([])
    setErrorText('')
  }

  const handleSubmit = async () => {
    setErrorText('')

    if (isDisabled) {
      setErrorText(t('double.bet.locked', 'Ставки временно закрыты'))
      return
    }

    if (!selectedColor) {
      setErrorText(t('double.bet.errorColor', 'Не выбран цвет ставки'))
      return
    }

    if (!parsedCrystal && selectedItems.length === 0) {
      setErrorText(t('double.bet.errorEmpty', 'Добавьте кристаллы или предметы'))
      return
    }

    if (totalBet < MIN_BET) {
      setErrorText(t('double.bet.errorMin', 'Минимальная ставка 100 кристаллов'))
      return
    }

    try {
      const success = await onSubmit?.({
        color: selectedColor,
        crystal: parsedCrystal,
        items: selectedItems
      })

      if (success) {
        clearForm()
      }
    } catch (error) {
      // Ошибки отображаются через toast в родителе
    }
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-surface/50 p-4 space-y-4">
      <h3 className="text-white font-semibold">{t('double.desktopBet.title', 'Быстрая ставка')}</h3>

      <div>
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="text-text-secondary">{t('double.bet.crystals', 'Кристаллы')}</span>
          <span className="inline-flex items-center gap-1 text-white font-semibold">
            <Gem className="w-4 h-4 text-accent-primary" />
            {Number(balance || 0).toLocaleString()}
          </span>
        </div>

        <input
          type="number"
          min="0"
          value={crystalValue}
          onChange={(event) => setCrystalValue(normalizeCrystal(event.target.value))}
          placeholder={t('double.bet.enterCrystals', 'Введите сумму')}
          className="w-full rounded-lg border border-white/10 bg-dark-primary px-3 py-2 text-white outline-none"
        />

        <div className="mt-2 grid grid-cols-5 gap-1.5">
          {[100, 500, 1000, 5000].map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => handleQuickCrystal(parsedCrystal + value)}
              className="rounded-md border border-white/10 bg-dark-primary px-2 py-1 text-xs text-text-secondary hover:text-white"
            >
              +{value}
            </button>
          ))}

          <button
            type="button"
            onClick={() => setCrystalValue('')}
            className="rounded-md border border-white/10 bg-dark-primary px-2 py-1 text-xs text-text-secondary hover:text-white inline-flex items-center justify-center"
            aria-label={t('double.bet.clearCrystals', 'Очистить поле кристаллов')}
            title={t('double.bet.clearCrystals', 'Очистить поле кристаллов')}
          >
            <Delete className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {hasInventory && (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm text-text-secondary">{t('double.bet.items', 'Предметы')}</p>
            <button
              type="button"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="rounded-md border border-white/10 bg-dark-primary p-1.5 text-text-secondary hover:text-white"
            >
              <ArrowUpDown className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-2 grid max-h-56 grid-cols-4 gap-2 overflow-y-auto pr-1">
            {filteredInventory.map((item) => {
              const isSelected = selectedItems.includes(item.id)
              const itemPrice = Number(item.price ?? item.cost ?? 0)

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => toggleItem(item.id)}
                  className={`relative rounded-lg border bg-dark-primary transition-all ${
                    isSelected
                      ? 'border-accent-primary shadow-md shadow-accent-primary/20'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute left-1 top-1 z-10">
                      <div className="flex h-4 w-4 items-center justify-center rounded-full bg-accent-primary">
                        <CheckCircle2 className="h-3 w-3 text-dark-primary" />
                      </div>
                    </div>
                  )}

                  <div className="aspect-square p-1.5">
                    <img src={item.img} alt={item.name} className="h-full w-full object-contain" loading="lazy" />
                  </div>

                  <div className="px-1.5 pb-1.5 text-center">
                    <p className="truncate text-[10px] text-white">{String(item.name || '').split('|')[0].trim()}</p>
                    <p className="mt-1 inline-flex w-full items-center justify-center gap-1 text-[10px] font-bold text-accent-primary">
                      <Gem className="h-3 w-3" />
                      {itemPrice.toLocaleString()}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {hasBetInput && (
        <>
          <div className="grid grid-cols-3 gap-2">
            {[
              { key: 'red', label: t('double.colors.red', 'Красный'), className: 'border-red-400/40 bg-red-500/20 text-red-100' },
              { key: 'black', label: t('double.colors.black', 'Чёрный'), className: 'border-slate-400/40 bg-slate-800 text-slate-100' },
              { key: 'green', label: t('double.colors.green', 'Зелёный'), className: 'border-emerald-400/40 bg-emerald-500/20 text-emerald-100' }
            ].map((option) => {
              const isActive = selectedColor === option.key
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSelectedColor(option.key)}
                  className={`rounded-lg border px-2 py-2 text-xs font-semibold transition-all ${option.className} ${isActive ? 'ring-2 ring-accent-primary/70' : 'opacity-80 hover:opacity-100'}`}
                >
                  {option.label}
                </button>
              )
            })}
          </div>

          {errorText && <p className="text-sm text-red-400">{errorText}</p>}

          <AnimatedButton onClick={handleSubmit} disabled={!canSubmit}>
            {isLoading
              ? t('double.bet.sending', 'Отправка...')
              : isDisabled
                ? t('double.bet.locked', 'Ставки закрыты')
                : !selectedColor
                  ? t('double.desktopBet.selectColor', 'Выберите цвет')
                  : totalBet < MIN_BET
                    ? t('double.bet.missingAmount', 'Не хватает {{amount}}', { amount: missingAmount.toLocaleString() })
                    : (
                      <span className="inline-flex items-center gap-2">
                        {t('double.bet.submit', 'Сделать ставку')}
                        <span className="inline-flex items-center gap-1">
                          <Gem className="w-4 h-4 text-dark-primary" />
                          {totalBet.toLocaleString()}
                        </span>
                      </span>
                    )}
          </AnimatedButton>
        </>
      )}
    </div>
  )
}

export default DoubleDesktopBetPanel
